import { dispatch } from '@/redux/index';
import { createBrowserClient } from '@/supabase/client';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getPatient,
  sendPatientQuest,
  startTranscription,
  updatePatientInfo,
} from '../../services/patient/patient.service';
import { getPatientsByTherapist } from '../../services/therapist/therapist.service';
import { safeJSONParse } from '../../utils/common';
import { transform } from '../../utils/transform';

export const fetchPatients = createAsyncThunk(
  'patients/fetchPatients',
  async (
    { therapist_id, page = 1 }: { therapist_id: string; page?: number },
    thunkAPI
  ) => {
    try {
      const { data, pagination }: any = await getPatientsByTherapist(
        therapist_id,
        page,
        10
      );
      return {
        data: transform.patient(data),
        pagination: {
          ...pagination,
          allRecords: transform.patient(pagination.allRecords),
        },
      };
    } catch (error) {
      return thunkAPI.rejectWithValue('Error fetching patients');
    }
  }
);

export const fetchPatient = createAsyncThunk(
  'patients/fetchPatient',
  async ({ patient_id }: { patient_id: string }, thunkAPI) => {
    try {
      const data: any = await getPatient(patient_id);

      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue('Error fetching patients');
    }
  }
);

export const refetchPatient = createAsyncThunk(
  'patients/refetchPatient',
  async ({ patient_id }: { patient_id: string }, thunkAPI) => {
    try {
      const data: any = await getPatient(patient_id);

      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue('Error fetching patients');
    }
  }
);

export const updatePatientInformation = createAsyncThunk(
  'patients/updatePatientInformation',
  async ({ patient_id, data }: { patient_id: string; data: any }, thunkAPI) => {
    try {
      const res: any = await updatePatientInfo(patient_id, data);

      if (!res) return thunkAPI.rejectWithValue('Error updating patient');

      return dispatch(fetchPatient({ patient_id }));
    } catch (error) {
      return thunkAPI.rejectWithValue('Error fetching patients');
    }
  }
);

export const sendPatientQuestionnaires = createAsyncThunk(
  'patients/sendPatientQuestionnaires',
  async (
    {
      patient_id,
      therapist_id,
      questionnaires,
    }: { patient_id: string; therapist_id: string; questionnaires: string[] },
    thunkAPI
  ) => {
    try {
      const res: any = await sendPatientQuest(
        patient_id,
        therapist_id,
        questionnaires
      );

      if (!res) return thunkAPI.rejectWithValue('Error updating patient');

      return dispatch(fetchPatient({ patient_id }));
    } catch (error) {
      return thunkAPI.rejectWithValue('Error fetching patients');
    }
  }
);

export const sendPatientNote = createAsyncThunk(
  'patients/sendPatientNote',
  async (
    { patient_id, data }: { patient_id: string; data: FormData },
    thunkAPI
  ) => {
    const supabase = createBrowserClient();
    try {
      const file = data.get('audio') as File | null;
      const title = data.get('title') as String | null;
      const content = data.get('content') as String | null;
      const symptoms: any[] = [];
      const therapist_id = data.get('therapist_id') as String | null;
      let url = null;

      data.forEach((value, key) => {
        if (key.startsWith('symptoms')) {
          symptoms.push(safeJSONParse(value, null));
        }
      });

      if (file) {
        const buffer = await file.arrayBuffer();
        const fileName = `${Date.now()}_${file.name}`;

        const { error } = await supabase.storage
          .from('notes-audio')
          .upload(fileName, buffer, {
            contentType: file.type,
          });

        if (error) return thunkAPI.rejectWithValue(error.message);

        const {
          data: { publicUrl },
        } = supabase.storage.from('notes-audio').getPublicUrl(fileName);

        if (!publicUrl) return thunkAPI.rejectWithValue('Audio not found.');

        url = publicUrl;
      }

      const { data: note, error: error_note } = await supabase
        .from('notes')
        .insert({
          title,
          content,
          symptomatology: symptoms,
          audio_path: url,
          therapist_id,
          patient_id,
        })
        .select()
        .single();

      if (error_note) return thunkAPI.rejectWithValue(error_note.message);

      if (file) {
        const { data: audio, error: error_audio } = await supabase
          .from('audio')
          .insert({ url_path: url, status: 'created' })
          .select()
          .single();

        if (error_audio) return thunkAPI.rejectWithValue(error_audio.message);

        const { error: error_note_update } = await supabase
          .from('notes')
          .update({ audio_id: audio.id })
          .eq('id', note.id);

        if (error_note_update)
          return thunkAPI.rejectWithValue(error_note_update.message);

        dispatch(startNoteTranscription({ patient_id, audio_id: audio.id }));
        return dispatch(fetchPatient({ patient_id }));
      }

      return dispatch(fetchPatient({ patient_id }));
    } catch (error) {
      return thunkAPI.rejectWithValue('Error interno del servidor');
    }
  }
);

export const startNoteTranscription = createAsyncThunk(
  'patients/startNoteTranscription',
  async (
    { patient_id, audio_id }: { patient_id: string; audio_id: string },
    thunkAPI
  ) => {
    try {
      const res: any = await startTranscription(audio_id);

      return dispatch(fetchPatient({ patient_id }));
    } catch (error) {
      return thunkAPI.rejectWithValue('Error fetching patients');
    }
  }
);
